import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "../../components/sidebar/Sidebar";
import "../../styles/sidebar/sidebar.css";
import "../../styles/dashboard/dashboard.css";
import Header from "../../components/header/Header";
import InfoCard from "../../components/cards/InfoCard";
import "../../styles/cards/card.css";
import { Icon } from "@iconify/react/dist/iconify.js";
import AddIndexedRecords from "../../components/cards/AddIndexedRecords";
import axios from "axios";
import Cookies from "js-cookie";
import Pagination from "../../components/pagination/Pagination";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { debounce } from "lodash";

const Dashboard = () => {
  const [loadingSites, setLoadingSites] = useState(true);
  const [loadingCards, setLoadingCards] = useState(true);
  const [siteData, setSiteData] = useState(null);
  const [error, setError] = useState(null);
  const [activeSites, setActiveSites] = useState([]);
  const [filteredSites, setFilteredSites] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 8;
  const [clerkStats, setClerkStats] = useState({
    totalIndexed: 0,
    pending: 0,
    declined: 0,
    approved: 0,
  });
  const [yellowFormStats, setYellowFormStats] = useState({
      totalScannedForms: 0,
      totalForms: 0,
      totalApprovedForms: 0,
      totalDeclined: 0,
      totalApproved: 0,
    });
  
  const baseUrl = process.env.REACT_APP_BASE_URL;

  // Fetch clerk stats
  useEffect(() => {
    const fetchClerkStats = async () => {
      try {
        const token = Cookies.get("token");
        const res = await axios.get(
          `${baseUrl}/api/index/indexed-records/stats`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setClerkStats(res.data);
      } catch (error) {
        setError("Failed to load clerk statistics");
      } finally {
        setLoadingCards(false);
      }
    };

    fetchClerkStats();
  }, [baseUrl]);

  useEffect(() => {
      const fetchScannedYellowFormStats = async () => {
        try {
          const token = Cookies.get("token");
          const res = await axios.get(
            `${baseUrl}/api/yellow-forms/stats/clerk`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          setYellowFormStats(res.data);
        } catch (error) {
          setError("Failed to load site data");
        } finally {
          setLoadingCards(false);
        }
      };
      fetchScannedYellowFormStats();
    }, [baseUrl]);

  useEffect(() => {
    const fetchActiveSites = async () => {
      try {
        const token = Cookies.get("token");
        const res = await axios.get(`${baseUrl}/api/sites/active`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setActiveSites(res.data);
        setFilteredSites(res.data);
      } catch (error) {
        setError("Failed to load active sites");
      } finally {
        setLoadingSites(false);
      }
    };

    fetchActiveSites();
  }, [baseUrl]);

  const debouncedSearch = useCallback(
    debounce((term) => {
      const filtered = activeSites.filter(
        (site) =>
          (site.district?.toLowerCase()?.includes(term) || false) ||
          (site.sector?.toLowerCase()?.includes(term) || false)
      );
      setFilteredSites(filtered);
    }, 500),
    [activeSites]
  );

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    debouncedSearch(term);
  };

  const fetchDetailedYellowFormStats = () => {
    return axios.get(
      `${baseUrl}/api/yellow-forms/stats/details/clerk`,
      {
        headers: { Authorization: `Bearer ${Cookies.get("token")}` },
      }
    );
  }

  const renderSkeletonCards = () => {
    return Array(3)
      .fill()
      .map((_, index) => (
        <div key={index} className="card" style={{ minWidth: "300px" }}>
          <div className="card-header">
            <Skeleton width={250} height={12} />
            <Skeleton width={60} height={40} />
          </div>
          <div className="line"></div>
          <div className="card-content">
            <div className="progress-bar-container">
              <Skeleton width="100%" height={10} />
              <Skeleton width={80} height={12} style={{ marginTop: "11px" }} />
            </div>
          </div>
        </div>
      ));
  };

  const renderSkeletonSites = () => {
    return Array(4)
      .fill()
      .map((_, index) => (
        <div
          key={index}
          className="new-site-container scanned-books-card indexed skeleton-card"
          style={{ backgroundColor: "transparent" }}
        >
          <div className="sector-info">
            <Skeleton width={150} height={20} />
            <Skeleton width={60} height={12} style={{ marginTop: "8px" }} />
          </div>
          <div className="add-button">
            <Skeleton width={180} height={40} borderRadius={43} />
          </div>
        </div>
      ));
  };

  const fetchDetailedStats = () =>
    axios.get(`${baseUrl}/api/index/indexed-records/stats/details/clerk`, {
      headers: { Authorization: `Bearer ${Cookies.get("token")}` },
    });

  return (
    <>
      <div className="dashboard-container">
        <Sidebar />
        <div className="main-content">
          <Header />
          <div className="dashboard-content">
            <div className="dashboard-cards">
              {loadingCards ? (
                renderSkeletonCards()
              ) : (
                <>
                  <InfoCard
                    value={`${clerkStats.approved}/${clerkStats.totalIndexed}`}
                    title="OVERALL INDEXED RECORDS"
                    progress={(clerkStats.approved / clerkStats.totalIndexed) * 100}
                    fetchDetails={fetchDetailedStats}
                  />
                  <InfoCard
                    value={`${clerkStats.pending}/${clerkStats.totalIndexed}`}
                    title="PENDING APPROVAL RECORDS"
                    progress={
                      clerkStats.totalIndexed > 0
                        ? (clerkStats.pending / clerkStats.totalIndexed) * 100
                        : 0
                    }
                    fetchDetails={fetchDetailedStats}
                  />
                  <InfoCard
                    value={`${yellowFormStats?.totalApproved}/${yellowFormStats?.totalScannedForms}`}
                    title="OVERALL INDEXED YELLOW FORMS"
                    progress={
                      (yellowFormStats?.totalApproved / yellowFormStats?.totalScannedForms) *
                      100
                    }
                    fetchDetails={fetchDetailedYellowFormStats}
                  />
                  <InfoCard
                    value={`${clerkStats.declined}/${clerkStats.totalIndexed}`}
                    title="DECLINED RECORDS"
                    progress={
                      clerkStats.totalIndexed > 0
                        ? (clerkStats.declined / clerkStats.totalIndexed) * 100
                        : 0
                    }
                    fetchDetails={fetchDetailedStats}
                  />
                </>
              )}
            </div>

            <div className="scanned-books-container">
              <div className="search-container">
                <div className="search">
                  <div className="search-icon">
                    <Icon
                      icon="proicons:search"
                      color="#078ECE"
                      width="16"
                      height="16"
                    />
                  </div>
                  <input
                    type="text"
                    placeholder="Search sector or district "
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                </div>
              </div>
              <div className="scanned-books-cards">
                {loadingSites ? (
                  renderSkeletonSites()
                ) : filteredSites.length > 0 ? (
                  filteredSites.map((site) => (
                    <AddIndexedRecords
                      key={site.id}
                      siteId={site.id}
                      sector={site.sector}
                      district={site.district}
                      province={site.province}
                    />
                  ))
                ) : (
                  <div className="no-sites-found">No sites found</div>
                )}
              </div>
              {!loadingSites && filteredSites.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalItems={filteredSites.length}
                  entriesPerPage={entriesPerPage}
                  onPageChange={setCurrentPage}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
